import { useLocation, useSearchParams } from "react-router-dom";
import { ThanksPageType } from "../../constants/enums";
import ThanksPage from "./ThanksPage";
import { isValidThanksPageType } from "./ThanksPageUtils";

/**
 * Type for navigation state
 */
interface LocationState {
  type?: ThanksPageType;
}

/**
 * Wrapper component for ThanksPage that determines which thank you page variant to display
 * based on query parameters or navigation state.
 *
 * Priority: URL query param 'type' > navigation state > DEFAULT
 *
 * @example
 * // Via query param
 * navigate('/thanks?type=onboarding')
 *
 * // Via state
 * navigate('/thanks', { state: { type: ThanksPageType.EKYC } })
 */
const ThanksPageWrapper = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Try to get type from query params first, then state
  const rawQueryType = searchParams.get("type");
  const queryType = rawQueryType ? rawQueryType.toLowerCase() : null; // Normalize to lowercase to handle case sensitivity
  const stateType = (location.state as LocationState)?.type ?? null; // Convert undefined to null

  let selectedType: ThanksPageType = ThanksPageType.DEFAULT;

  if (isValidThanksPageType(queryType)) {
    selectedType = queryType;
  } else if (isValidThanksPageType(stateType)) {
    selectedType = stateType;
  } else {
    // Log warning for invalid types to aid debugging
    if (queryType !== null) {
      console.warn(
        `[ThanksPageWrapper] Invalid ThanksPageType in query params: "${queryType}". Defaulting to "${ThanksPageType.DEFAULT}"`
      );
    }
  }

  return <ThanksPage type={selectedType} />;
};

export default ThanksPageWrapper;
